<template>
  <div class="base-textarea" :class="{ 'has-danger': error }">
    <label v-if="label">{{ label }} {{ required ? '*' : '' }}</label>
    <el-input
      type="textarea"
      :value="value"
      :rows="rows"
      :maxlength="maxlength"
      :show-word-limit="showWordLimit"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="onInput"
    />
    <label v-if="error" class="error">{{ error }}</label>
  </div>
</template>

<script setup lang="ts">
// Themed multi-line text input that matches base-input's look (transparent
// background, same border as .form-control, primary focus ring) with light and
// dark parity. Use for any capped free-text field instead of a bare el-input
// textarea plus a per-page style block.
defineProps({
  value: { type: String, default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  rows: { type: Number, default: 3 },
  maxlength: { type: Number, default: 500 },
  showWordLimit: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
  error: { type: String, default: '' }
})

const emit = defineEmits<{ (e: 'input', value: string): void }>()

function onInput(val: string) {
  emit('input', val)
}
</script>

<script lang="ts">
export default { name: 'BaseTextarea' }
</script>

<style lang="scss" scoped>
// Border and radius mirror .form-control (dark: lighten($black, 10%) = #333f64,
// focus: $primary) so this sits flush next to base-input fields.
.base-textarea ::v-deep .el-textarea__inner {
  background-color: transparent;
  border-color: #333f64;
  color: #ffffff;
  border-radius: 0.4285rem;
  font-family: inherit;
  // Element positions the counter absolutely in the bottom-right corner, so
  // text that reaches the last line runs underneath it. Reserve the corner.
  padding-bottom: 22px;
}

.base-textarea ::v-deep .el-textarea__inner:focus {
  border-color: #e14eca;
}

.base-textarea.has-danger ::v-deep .el-textarea__inner {
  border-color: #ec250d;
}

.base-textarea.has-danger .error {
  color: #ec250d;
}

.base-textarea ::v-deep .el-textarea__inner::placeholder {
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
}

.base-textarea ::v-deep .el-input__count {
  background: transparent;
  color: rgba(255, 255, 255, 0.4);
}

// Light mode mirrors white-content .form-control (border rgba($black-states, 0.5)).
.white-content .base-textarea ::v-deep .el-textarea__inner {
  border-color: rgba(29, 37, 59, 0.5);
  color: rgba(0, 0, 0, 0.85);
}

.white-content .base-textarea ::v-deep .el-textarea__inner::placeholder {
  color: rgba(0, 0, 0, 0.35);
}

.white-content .base-textarea ::v-deep .el-input__count {
  color: rgba(0, 0, 0, 0.4);
}
</style>
